import { t } from "@lingui/macro";
import {
  ExpensesWhereColumn,
  OiWhereColumn,
  PlannedExpensesOrderByColumnEnum,
  SqlOperator,
} from "@src/__generated__/graphql";
import {
  AmountCell,
  BillingProfitSummaryRow,
  PlannedExpensesSummaryRow,
} from "@src/components/modules/projects/planned-expenses/components";
import type { PlannedExpensesTableRow } from "@src/components/modules/projects/planned-expenses/models";
import { StorePlannedExpenses } from "@src/components/modules/projects/planned-expenses/store";
import {
  ACTIONS_COLUMN_ID,
  Avatar,
  type BodyColumnDef,
  DataTable,
  Link,
  LoadingOverlay,
  UserTooltip,
} from "@src/components/ui-kit";
import { Pagination } from "@src/components/ui-kit/table/pagination";
import { ProjectCell } from "@src/components/widgets/TableCell";
import { FiltersPanel } from "@src/utils/components/filters/components/filters-panel";
import { Filter, Filters } from "@src/utils/components/filters/models";
import { withCan } from "@src/utils/components/permissions";
import { currency } from "@src/utils/formatters";
import { useStore, useUpdateURI } from "@src/utils/hooks";
import { observer, useLocalObservable } from "mobx-react-lite";
import { withProjectHeaderTooltip } from "../../reports/project/common";

export const PlannedExpensesPage = withCan(
  { do: ["project_read_all", "project_read_own", "project_read_team"] },
  observer(function PlannedExpensesPage() {
    const { workspaceStore } = useStore();
    const { store, columns } = useLocalObservable(() => ({
      store: new StorePlannedExpenses(),
      columns: createColumns(
        workspaceStore.settings?.planned_expenses_show_client_price ?? false,
      ),
    }));

    useUpdateURI(store.searchParams);

    return (
      <DataTable>
        <DataTable.Filter>
          <FiltersPanel
            filters={store.where}
            searchPlaceholder={t`Search by title, code, brand`}
            onSearch={store.handleSearch}
            searchDefaultValue={store.searchTerm}
          />
        </DataTable.Filter>

        <DataTable.SummaryBox hideBg>
          <DataTable.SummaryItem label={t`Budgeted`}>
            <BillingProfitSummaryRow
              label={t`Total price`}
              amount={store.summary?.total_budgeted_with_commission ?? 0}
              isLoading={store.isLoadingSummary}
              bottomMargin
            />

            {workspaceStore.settings?.planned_expenses_show_client_price && (
              <BillingProfitSummaryRow
                label={t`Client price`}
                amount={store.summary?.total_budget ?? 0}
                isLoading={store.isLoadingSummary}
                bottomMargin
              />
            )}

            <BillingProfitSummaryRow
              label={t`Buy price`}
              amount={store.summary?.total_buy_price ?? 0}
              isLoading={store.isLoadingSummary}
            />
          </DataTable.SummaryItem>

          <DataTable.SummaryItem label={t`Planned expenses`}>
            {workspaceStore.settings?.planned_expenses_show_client_price && (
              <PlannedExpensesSummaryRow
                progressBarLabel={t`vs Client price`}
                amount={store.summary?.total_planned_client_amount ?? 0}
                percentage={store.summary?.planned_client_amount_percentage}
                isLoading={store.isLoadingSummary}
                bottomMargin
                overHundredPercentColor="red.500"
              />
            )}

            <PlannedExpensesSummaryRow
              progressBarLabel={t`vs Buy price`}
              amount={store.summary?.total_planned_budgeted_amount ?? 0}
              percentage={store.summary?.planned_budgeted_amount_percentage}
              isLoading={store.isLoadingSummary}
              overHundredPercentColor="red.500"
            />
          </DataTable.SummaryItem>

          <DataTable.SummaryItem label={t`Received expenses`}>
            <PlannedExpensesSummaryRow
              percentage={store.summary?.expenses_received_percentage}
              amount={store.summary?.total_expenses_received ?? 0}
              isLoading={store.isLoadingSummary}
              overHundredPercentColor="red.500"
            />
          </DataTable.SummaryItem>

          <DataTable.SummaryItem label={t`Billing`}>
            <BillingProfitSummaryRow
              label={t`Invoiced`}
              amount={store.summary?.total_invoiced_amount ?? 0}
              percentage={store.summary?.invoiced_amount_percentage ?? 0}
              isLoading={store.isLoadingSummary}
              bottomMargin
            />

            <BillingProfitSummaryRow
              label={t`Uninvoiced`}
              amount={store.summary?.uninvoiced_amount ?? 0}
              percentage={store.summary?.uninvoiced_amount_percentage ?? 0}
              isLoading={store.isLoadingSummary}
            />
          </DataTable.SummaryItem>

          <DataTable.SummaryItem label={t`Profit`}>
            <BillingProfitSummaryRow
              label={t`Estimated`}
              amount={store.summary?.profit_estimate ?? 0}
              percentage={store.summary?.profit_estimate_percentage ?? 0}
              isLoading={store.isLoadingSummary}
              bottomMargin
            />

            <BillingProfitSummaryRow
              label={t`Actual`}
              amount={store.summary?.actual_profit_amount ?? 0}
              percentage={store.summary?.actual_profit_percentage ?? 0}
              isLoading={store.isLoadingSummary}
            />
          </DataTable.SummaryItem>
        </DataTable.SummaryBox>

        <LoadingOverlay isLoading={store.isLoading} position="absolute">
          <DataTable.Body
            tableKey="planned-expenses"
            data={store.tableData}
            columns={columns}
            orderedBy={store.activeOrderBy}
            onOrderByChange={store.handleOrderChange}
            defaultHiddenColumnIds={
              workspaceStore.settings?.planned_expenses_show_client_price
                ? ["clientPrice", "plannedClientAmount"]
                : []
            }
          />
        </LoadingOverlay>

        <Pagination store={store.pagination} />
      </DataTable>
    );
  }),
);

const isProjectRow = (
  row: PlannedExpensesTableRow,
): row is Extract<PlannedExpensesTableRow, { rowType: "project" }> =>
  row.rowType === "project";

export function createColumns(
  showClientPrice: boolean,
): BodyColumnDef<PlannedExpensesTableRow, PlannedExpensesOrderByColumnEnum>[] {
  const columns: BodyColumnDef<
    PlannedExpensesTableRow,
    PlannedExpensesOrderByColumnEnum
  >[] = [
    {
      id: "projectManager",
      size: 50,
      meta: { label: t`PM` },
      header: t`PM`,
      cell: ({ row }) => {
        if (!isProjectRow(row.original)) {
          return null;
        }

        const { projectManager } = row.original.project.project;

        return (
          <UserTooltip user={projectManager}>
            <Avatar
              name={projectManager?.full_name}
              src={projectManager?.image?.urls?.thumbnail ?? undefined}
              colorScheme={projectManager?.profile?.hex_color}
            />
          </UserTooltip>
        );
      },
    },
    {
      id: "title",
      meta: { label: t`Project` },
      header: t`Project`,
      orderBy: PlannedExpensesOrderByColumnEnum.ProjectCode,
      cell: ({ row }) => {
        if (isProjectRow(row.original)) {
          const project = row.original.project.project;
          return <ProjectCell.Title project={project} brand={project.brand} />;
        }

        const { subRow } = row.original;
        return (
          <span>
            {subRow.title} - {subRow.category_name}
          </span>
        );
      },
    },
    {
      id: "totalBudgetedWithCommission",
      meta: { label: t`Total price (with commission)`, align: "right" },
      header: withProjectHeaderTooltip(
        t`Total price (with commission)`,
        t`Total client price of the item, including commission.`,
      ),
      orderBy: PlannedExpensesOrderByColumnEnum.TotalBudgetedWithCommission,
      cell: ({ row }) => {
        const amount = isProjectRow(row.original)
          ? row.original.project.total_budgeted_with_commission
          : row.original.subRow.total_budgeted_with_commission;
        return <AmountCell amount={amount} />;
      },
    },
  ];

  if (showClientPrice) {
    columns.push({
      id: "clientPrice",
      meta: { label: t`Client price`, align: "right" },
      header: t`Client price`,
      cell: ({ row }) => {
        const amount = isProjectRow(row.original)
          ? row.original.project.total_budget
          : row.original.subRow.total_budget;
        return <AmountCell amount={amount} />;
      },
    });
  }

  columns.push(
    {
      id: "totalBuyPrice",
      meta: { label: t`Buy price`, align: "right" },
      header: withProjectHeaderTooltip(
        t`Buy price`,
        t`Expected agency cost for the item.`,
      ),
      orderBy: PlannedExpensesOrderByColumnEnum.TotalBuyPrice,
      cell: ({ row }) => {
        const amount = isProjectRow(row.original)
          ? row.original.project.total_buy_price
          : row.original.subRow.buy_price;
        return <AmountCell amount={amount} />;
      },
    },
    {
      id: "profitEstimate",
      meta: { label: t`Profit estimate`, align: "right" },
      header: withProjectHeaderTooltip(
        t`Profit estimate`,
        t`Total price with commission − Buy price.`,
      ),
      cell: ({ row }) => {
        const amount = isProjectRow(row.original)
          ? row.original.project.profit_estimate
          : row.original.subRow.profit_estimate;
        return <AmountCell amount={amount} />;
      },
    },
  );

  if (showClientPrice) {
    columns.push({
      id: "plannedClientAmount",
      meta: { label: t`Planned expenses (vs Client price)`, align: "right" },
      header: withProjectHeaderTooltip(
        t`Planned expenses (vs Client price)`,
        t`Costs already planned for this item.`,
      ),
      orderBy: PlannedExpensesOrderByColumnEnum.TotalPlannedClientAmount,
      cell: ({ row }) => {
        const amount = isProjectRow(row.original)
          ? row.original.project.total_planned_client_amount
          : row.original.subRow.planned_client_amount;
        return <AmountCell amount={amount} />;
      },
    });
  }

  columns.push({
    id: "plannedBudgetedAmount",
    meta: {
      label: showClientPrice
        ? t`Planned expenses (vs Buy price)`
        : t`Planned expenses`,
      align: "right",
    },
    header: withProjectHeaderTooltip(
      showClientPrice
        ? t`Planned expenses (vs Buy price)`
        : t`Planned expenses`,
      t`Costs already planned for this item.`,
    ),
    orderBy: PlannedExpensesOrderByColumnEnum.TotalPlannedBudgetedAmount,
    cell: ({ row }) => {
      const amount = isProjectRow(row.original)
        ? row.original.project.total_planned_budgeted_amount
        : row.original.subRow.planned_budgeted_amount;
      return <AmountCell amount={amount} />;
    },
  });

  if (showClientPrice) {
    columns.push({
      id: "toPlanClientPrice",
      meta: { label: t`To plan (Client price)`, align: "right" },
      header: withProjectHeaderTooltip(
        t`To plan (Client price)`,
        t`Client price − Planned expenses.`,
      ),
      cell: ({ row }) => {
        const amount = isProjectRow(row.original)
          ? row.original.project.to_plan_client_price
          : row.original.subRow.to_plan_client_price;
        return <AmountCell amount={amount} />;
      },
    });
  }

  columns.push(
    {
      id: "toPlanBuyPrice",
      meta: {
        label: showClientPrice ? t`To plan (Buy price)` : t`To plan`,
        align: "right",
      },
      header: withProjectHeaderTooltip(
        showClientPrice ? t`To plan (Buy price)` : t`To plan`,
        t`Buy price − Planned expenses.`,
      ),
      cell: ({ row }) => {
        const amount = isProjectRow(row.original)
          ? row.original.project.to_plan_buy_price
          : row.original.subRow.to_plan_buy_price;
        return <AmountCell amount={amount} />;
      },
    },
    {
      id: "expensesReceived",
      meta: { label: t`Expenses received`, align: "right" },
      header: withProjectHeaderTooltip(
        t`Expenses received`,
        t`Real expenses already received or recorded for this item.`,
      ),
      orderBy: PlannedExpensesOrderByColumnEnum.TotalExpensesReceived,
      cell: ({ row }) => {
        const amount = isProjectRow(row.original)
          ? row.original.project.total_expenses_received
          : row.original.subRow.expenses_received;

        const filters = new Filters<ExpensesWhereColumn>([
          new Filter({
            column: ExpensesWhereColumn.ProjectId,
            operator: SqlOperator.Eq,
            value: [row.original.project.id],
            options: [],
          }),
        ]);

        return (
          <Link
            href={{
              pathname: "/expenses",
              query: filters.asURLSearchParam,
            }}
            target="_blank"
            color={amount < 0 ? "red.500" : undefined}
          >
            {currency.formatByWorkspace(amount)}
          </Link>
        );
      },
    },
    {
      id: "invoicedAmount",
      meta: { label: t`Invoiced`, align: "right" },
      header: withProjectHeaderTooltip(
        t`Invoiced`,
        t`Amount already invoiced to the client for this item.`,
      ),
      orderBy: PlannedExpensesOrderByColumnEnum.TotalInvoicedAmount,
      cell: ({ row }) => {
        const amount = isProjectRow(row.original)
          ? row.original.project.total_invoiced_amount
          : row.original.subRow.invoiced_amount;

        const filters = new Filters<OiWhereColumn>([
          new Filter({
            column: OiWhereColumn.ProjectId,
            operator: SqlOperator.Eq,
            value: [row.original.project.id],
            options: [],
          }),
        ]);

        return (
          <Link
            href={{
              pathname: "/invoices/outgoing-invoices",
              query: filters.asURLSearchParam,
            }}
            color={amount < 0 ? "red.500" : undefined}
            target="_blank"
          >
            {currency.formatByWorkspace(amount)}
          </Link>
        );
      },
    },
    {
      id: "uninvoicedAmount",
      meta: { label: t`Uninvoiced`, align: "right" },
      header: withProjectHeaderTooltip(
        t`Uninvoiced`,
        t`Total price with commission − Invoiced.`,
      ),
      cell: ({ row }) => {
        const amount = isProjectRow(row.original)
          ? row.original.project.uninvoiced_amount
          : row.original.subRow.uninvoiced_amount;
        return <AmountCell amount={amount} />;
      },
    },
    {
      id: "actualProfitAmount",
      meta: { label: t`Actual profit`, align: "right" },
      header: withProjectHeaderTooltip(
        t`Actual profit`,
        t`Current real profit: Invoiced − Expenses received.`,
      ),
      cell: ({ row }) => {
        const amount = isProjectRow(row.original)
          ? row.original.project.actual_profit_amount
          : row.original.subRow.actual_profit_amount;
        return <AmountCell amount={amount} />;
      },
    },
    {
      id: ACTIONS_COLUMN_ID,
    },
  );

  return columns;
}
